import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, CheckCircle, Clock, Lock, TrendingUp, AlertTriangle, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { useTrainingModules } from '../hooks/useTrainingModules';
import { useTrainingAssignments } from '../hooks/useTrainingAssignments';
import { useFailedQuizAttempts } from '../hooks/useFailedQuizAttempts';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
export const ProgressDashboard: React.FC = () => {
  const { modules } = useTrainingModules();
  const { assignments, sellers } = useTrainingAssignments();
  const { failedAttempts, unreviewedCount, markAsReviewed } = useFailedQuizAttempts();
  const [expandedAttempt, setExpandedAttempt] = useState<string | null>(null);
  const [showFailedSection, setShowFailedSection] = useState(false);

  // Calculate progress per seller
  const sellerProgress = sellers.map(seller => {
    const sellerAssignments = assignments.filter(a => a.seller_id === seller.user_id);
    const completed = sellerAssignments.filter(a => a.status === 'completed').length;
    const total = sellerAssignments.length;
    const inProgress = sellerAssignments.find(a => a.status === 'in_progress' || a.status === 'pending');
    
    const currentModule = inProgress 
      ? modules.find(m => m.id === inProgress.module_id)
      : null;

    return {
      ...seller,
      assignments: sellerAssignments,
      completed,
      total,
      percentage: total > 0 ? (completed / total) * 100 : 0,
      currentModule,
      currentAssignment: inProgress,
    };
  }).filter(s => s.total > 0); // Only show sellers with assignments

  const totalCompleted = assignments.filter(a => a.status === 'completed').length;

  // Get quizzes for failed attempts to show correct answers
  const getQuizzesForModule = (moduleId: string) => {
    return modules.find(m => m.id === moduleId);
  };

  return (
    <div className="space-y-4">
      {/* Stats Header */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
          <CheckCircle className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
          <p className="text-2xl font-bold text-emerald-400">{totalCompleted}</p>
          <p className="text-white/50 text-xs">Completados</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-center">
          <Clock className="w-6 h-6 text-blue-400 mx-auto mb-1" />
          <p className="text-2xl font-bold text-blue-400">
            {assignments.filter(a => a.status === 'in_progress' || a.status === 'pending').length}
          </p>
          <p className="text-white/50 text-xs">En Progreso</p>
        </div>
        <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 text-center">
          <Users className="w-6 h-6 text-purple-400 mx-auto mb-1" />
          <p className="text-2xl font-bold text-purple-400">{sellerProgress.length}</p>
          <p className="text-white/50 text-xs">Vendedores</p>
        </div>
      </div>

      {/* Failed Attempts Section */}
      {failedAttempts.length > 0 && (
        <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl overflow-hidden">
          <button
            onClick={() => setShowFailedSection(!showFailedSection)}
            className="w-full p-4 flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              <span className="text-white font-medium">Intentos Fallidos</span>
              {unreviewedCount > 0 && (
                <span className="px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">
                  {unreviewedCount} sin revisar
                </span>
              )}
            </div>
            {showFailedSection ? (
              <ChevronUp className="w-5 h-5 text-white/50" />
            ) : (
              <ChevronDown className="w-5 h-5 text-white/50" />
            )}
          </button>

          <AnimatePresence>
            {showFailedSection && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-orange-500/20"
              >
                <div className="p-3 space-y-2 max-h-80 overflow-y-auto">
                  {failedAttempts.map(attempt => (
                    <div
                      key={attempt.id}
                      className={`bg-white/5 rounded-lg border ${
                        attempt.reviewed_by_admin ? 'border-white/10' : 'border-orange-500/30'
                      }`}
                    >
                      <button
                        onClick={() => setExpandedAttempt(expandedAttempt === attempt.id ? null : attempt.id)}
                        className="w-full p-3 text-left"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-medium text-sm truncate">{attempt.seller_name}</p>
                            <p className="text-white/50 text-xs truncate">{attempt.module_title}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-red-400 text-xs font-medium">
                              {attempt.score}/{attempt.total_questions}
                            </span>
                            <span className="text-white/40 text-xs">
                              {format(new Date(attempt.attempted_at), 'dd MMM', { locale: es })}
                            </span>
                            {expandedAttempt === attempt.id ? (
                              <ChevronUp className="w-4 h-4 text-white/40" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-white/40" />
                            )}
                          </div>
                        </div>
                      </button>

                      <AnimatePresence>
                        {expandedAttempt === attempt.id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="border-t border-white/10 p-3 space-y-2"
                          >
                            <p className="text-white/60 text-xs">
                              Respuestas del vendedor: {attempt.answers.map((a, i) => (
                                <span key={i} className="inline-block mx-1 px-1.5 py-0.5 bg-white/10 rounded text-white/80">
                                  P{i + 1}: Opción {String.fromCharCode(65 + a)}
                                </span>
                              ))}
                            </p>
                            
                            {!attempt.reviewed_by_admin && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsReviewed.mutate(attempt.id);
                                }}
                                className="w-full text-xs"
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Marcar como revisado
                              </Button>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Seller Progress List */}
      <div className="space-y-3">
        <h4 className="text-white/70 text-sm font-medium flex items-center gap-2">
          <TrendingUp className="w-4 h-4" />
          Progreso por Vendedor
        </h4>

        {sellerProgress.length === 0 ? (
          <div className="text-center py-8 bg-white/5 rounded-xl">
            <Users className="w-12 h-12 mx-auto mb-3 text-white/20" />
            <p className="text-white/50">Sin asignaciones activas</p>
            <p className="text-white/30 text-sm">Asigna módulos a los vendedores</p>
          </div>
        ) : (
          sellerProgress.map(seller => (
            <motion.div
              key={seller.user_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/5 border border-white/10 rounded-xl p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h5 className="text-white font-medium">{seller.nombre}</h5>
                  {seller.ubicacion_tienda && (
                    <p className="text-white/50 text-xs">{seller.ubicacion_tienda}</p>
                  )}
                </div>
                <div className="text-right">
                  <span className="text-emerald-400 font-bold">{seller.completed}</span>
                  <span className="text-white/50">/{seller.total}</span>
                </div>
              </div>

              <Progress 
                value={seller.percentage} 
                className="h-2 mb-3"
              />

              {seller.currentModule && seller.currentAssignment && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <Clock className="w-4 h-4 text-blue-400 shrink-0" />
                    <span className="text-white/70 shrink-0">Actual:</span>
                    <span className="text-blue-400 truncate">{seller.currentModule.title}</span>
                  </div>
                  {seller.currentAssignment.expected_completion && (
                    <span className="text-white/40 text-xs sm:ml-auto pl-6 sm:pl-0">
                      Límite: {format(new Date(seller.currentAssignment.expected_completion), 'dd MMM', { locale: es })}
                    </span>
                  )}
                </div>
              )}

              {seller.percentage === 100 && (
                <div className="flex items-center gap-2 text-sm text-emerald-400">
                  <CheckCircle className="w-4 h-4" />
                  ¡Entrenamiento completado!
                </div>
              )}

              {/* Module breakdown */}
              <div className="mt-3 flex flex-wrap gap-1">
                {seller.assignments
                  .sort((a, b) => {
                    const moduleA = modules.find(m => m.id === a.module_id);
                    const moduleB = modules.find(m => m.id === b.module_id);
                    return (moduleA?.order_position ?? 0) - (moduleB?.order_position ?? 0);
                  })
                  .map((assignment, index) => {
                    const statusColors = {
                      completed: 'bg-emerald-500/30 text-emerald-400',
                      in_progress: 'bg-blue-500/30 text-blue-400',
                      pending: 'bg-yellow-500/30 text-yellow-400',
                      locked: 'bg-white/10 text-white/30',
                    };
                    return (
                      <div
                        key={assignment.id}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-medium ${statusColors[assignment.status]}`}
                        title={modules.find(m => m.id === assignment.module_id)?.title}
                      >
                        {assignment.status === 'locked' ? (
                          <Lock className="w-3 h-3" />
                        ) : assignment.status === 'completed' ? (
                          <CheckCircle className="w-3.5 h-3.5" />
                        ) : (
                          index + 1
                        )}
                      </div>
                    );
                  })}
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};
